import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';

import { useCurrentLanguage } from 'contexts/CurrentLanguageContext';
import { isEqual, omit } from 'lodash';
import { useLocation, useSearchParams } from 'react-router-dom';
import { autorun } from 'mobx';
import { allCurrenciesSymbol } from 'stores/constants';
import StoreUrlSync from './storeUrlSync';

/**
 * Get nested attribute from object
 * rgetattr(obj, 'test.attr1') == obj.test.attr1
 * @param {object} obj
 * @param {string} attrName
 * @param {string} separator
 */
export const rgetattr = (obj, attrName, separator = '.') => {
  const attrList = attrName.split(separator);
  let value = obj && obj[attrList[0]];
  for (let i = 1; i < attrList.length; ++i) value = value && value[attrList[i]];
  return value;
};

export function assert(condition: boolean, msg: string = 'Assertion Error'): asserts condition {
  if (!condition) {
    throw new Error(msg);
  }
}

export const currencyDisplay = (currencyISO: Currency) => allCurrenciesSymbol[currencyISO] || currencyISO;

export const currentLanguage = (i18nObject?: I18nField | null, lang?: Lang): string => {
  if (i18nObject === null || i18nObject === undefined) return '';

  if (typeof i18nObject === 'string') {
    return i18nObject;
  }

  // Helper function to get value with fallback to base language
  const getLangWithFallback = (langCode: Lang): string | undefined => {
    const value = i18nObject[langCode];
    if (value) return value;

    // Try base language if regional variant (e.g., 'de-at' -> 'de')
    if (langCode.includes('-')) {
      const baseLang = langCode.split('-')[0] as Lang;
      return i18nObject[baseLang];
    }
    return undefined;
  };

  // Try provided lang parameter first
  if (lang) {
    const value = getLangWithFallback(lang);
    if (value) return value;
  }

  // Try window.appState.lang
  const appLang = window.appState.lang as Lang;
  const appValue = getLangWithFallback(appLang);
  if (appValue) return appValue;

  // Finally, fallback to first truthy value
  return Object.values(i18nObject).find((a) => !!a) || '';
};

export const useI18nToStr = () => {
  const lang = useCurrentLanguage();
  return (i18nObject?: I18nField | null) => currentLanguage(i18nObject, lang) || '';
};

export const arrayMove = (items: any[], oldIndex: number, newIndex: number) => {
  const copiedItems = [...items]; // shallow copy
  const [tmp] = copiedItems.splice(oldIndex, 1);
  copiedItems.splice(newIndex, 0, tmp);
  return copiedItems;
};

export function changeCollection<T>(col1: T[], col2: T[], oldIndex: number, newIndex: number): [T[], T[]] {
  const copyCol1 = [...col1]; // shallow copy
  const copyCol2 = [...col2]; // shallow copy
  const [tmp] = copyCol1.splice(oldIndex, 1); // remove from old collection
  copyCol2.splice(newIndex, 0, tmp); // insert to new
  return [copyCol1, copyCol2];
}

export const convertDecimal = (price: number | Decimal, currency: CurrencyField) => Number(price) / 100;

export const formatPrice = (price: number | Decimal, currency: CurrencyField, isDecimal: boolean = false) => {
  if (isDecimal) price = convertDecimal(price, currency);

  try {
    const fractionDigits = price % 1 === 0 ? 0 : undefined;
    const langCode = window.appState.lang;
    const priceFormatter = Intl.NumberFormat(langCode, {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    return priceFormatter.format(price);
  } catch (error) {
    console.error(error);
    return `${price} ${currency}`;
  }
};

export function nl2br(str: string | null) {
  str = str || '';
  const newlineRegex = /(\r\n|\r|\n)/g;

  if (typeof str === 'number') {
    return str;
  }

  if (typeof str !== 'string') {
    return '';
  }

  return str.split(newlineRegex).map((line, index) => {
    if (line.match(newlineRegex)) {
      return React.createElement('br', { key: index });
    }
    return line;
  });
}

export function nl2p(str: string) {
  return str
    .split('\n')
    .map((item) => `<p>${item}</p>`)
    .join('');
}

export function stripHtml(html: string) {
  return new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() || '';
}

export const usePrevValue = <T extends object>(props: T) => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = props;
  });
  return ref.current;
};

export const isProductionEnvironment = () => process.env.NODE_ENV === 'production';

export const useRerender = () => {
  const [, setValue] = useState(false);
  const rerender = useCallback(() => setValue((v) => !v), []);
  return rerender;
};

export const getForegroundColor = (backgroundColor: string) => {
  /* This will be black or white - the one that will be more suitable for selected background color */
  const darkColor = '#000000';
  const lightColor = '#ffffff';

  // creates array ["RR", "GG", "BB"]
  const parsedColor = backgroundColor.slice(1).match(/.{2}/g);
  if (parsedColor === null || parsedColor.length !== 3) return darkColor;

  // applies this magic formula 'if (red*0.299 + green*0.587 + blue*0.114) > 186 use #000000 else use #ffffff'
  const backgroundColorIntensivness = parsedColor
    .map((x) => parseInt(x, 16))
    .reduce((acc, curr, i) => acc + curr * [0.299, 0.587, 0.114][i], 0);

  return backgroundColorIntensivness > 150 ? darkColor : lightColor;
};

export const isUUID = (str: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str);

export const roundHalfUp = (value: number | string, decimals: number) => {
  const decimal = Math.round(Number(`${value}e${decimals}`));
  return Number(`${decimal}e-${decimals}`);
};

export function useContextSafe<T>(context: React.Context<T>): NonNullable<T> {
  const value = useContext(context);

  if (value === null || value === undefined) {
    throw Error(`Context ${context} must be used within context provider!`);
  }

  return value as NonNullable<T>;
}

type IndexableKeys<T> = { [K in keyof T]: T[K] extends string ? K : never }[keyof T];
type IndexedObject<T extends object> = { [key: string]: T | undefined };
export const indexArrayByProp = <T extends object, K extends IndexableKeys<T>>(array: T[], prop: K) =>
  array.reduce((acc, curr: T) => {
    acc[curr[prop]] = curr;
    return acc;
  }, {} as IndexedObject<T>);

export const useAsyncEffect = (func: () => Promise<void>, deps: any[]) =>
  useEffect(() => {
    func();
  }, deps);

export const formatDateAsMomentCalendar = (date: DateField) =>
  moment(date).calendar(null, {
    sameDay: `[${gettext('Today')}]`,
    nextDay: `[${gettext('Tomorrow')}]`,
    nextWeek: 'LL',
    lastDay: `[${gettext('Yesterday')}]`,
    lastWeek: 'LL',
    sameElse: 'LL',
  });

export const weekDays = [
  gettext('Monday'),
  gettext('Tuesday'),
  gettext('Wednesday'),
  gettext('Thursday'),
  gettext('Friday'),
  gettext('Saturday'),
  gettext('Sunday'),
];

export const monthNames = [
  gettext('January'),
  gettext('February'),
  gettext('March'),
  gettext('April'),
  gettext('May'),
  gettext('June'),
  gettext('July'),
  gettext('August'),
  gettext('September'),
  gettext('October'),
  gettext('November'),
  gettext('December'),
];

export const getNextNDays = (count: 1 | 7): [number, string][] => {
  const today = new Date();
  const addDays = (days: number) => {
    const d = new Date(today.valueOf());
    d.setDate(d.getDate() + days);
    return d;
  };
  const getDayIndex = (offset: number) => (offset + today.getDay()) % 7;
  const getDayName = (offset: number) =>
    `${getDayIndex(offset) === today.getDay() ? gettext('Tomorrow') : weekDays[getDayIndex(offset)]}, ${moment(
      addDays(offset + 1),
    ).format('D. M.')}`;
  return [...Array(count).keys()].map((i) => [getDayIndex(i), getDayName(i)]);
};

export function compareObjects(q1: object, q2: object, excludeFields: string[] = []) {
  return isEqual(omit(q1, excludeFields), omit(q2, excludeFields));
}

export function compareQueries(q1: object, q2: object, excludeFields: string[] = []) {
  return compareObjects(q1, q2, [...excludeFields, 'page', 'search']);
}

export function toggleItem<T>(list: T[] | undefined, updatedElement: T) {
  if (!list) {
    return [updatedElement];
  }
  return list.includes(updatedElement) ? list.filter((item) => item !== updatedElement) : [...list, updatedElement];
}

export function makeAbsoluteUrl(url: string | undefined) {
  if (url) {
    const defaultProtocol = 'https';
    if (!url.match(/^(https?:)?\/\//)) {
      return `${defaultProtocol}://${url}`;
    }
  }
  return url;
}

export function isUrlValid(url: string) {
  const urlRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/gm;
  return urlRegex.test(url);
}

export function isImage(filename: string | undefined | null) {
  if (filename === undefined || filename === null) return false;
  return filename.match(/(.*\.jpeg|.*\.jpg|.*\.png|.*\.gif|.*\.svg)/);
}

export const excerpt = (s: string, len: number) => (s.length > len ? `${s.substr(0, len - 3)}...` : s);

export const formatNumber = (n: number) => new Intl.NumberFormat(window.appState.lang).format(n);

export const formatLongDate = (isoDate: string) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString(window.appState.lang, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export function getOrderPortalDirectLink(companyId: UUIDField, email: string, orderCode: string, lang?: string | null) {
  return `${window.location.origin}/${lang}/order-tracking/${companyId}/direct-link/?email=${email}&code=${orderCode}`;
}

export function useStoreUrlConnect<T extends TBaseAPIModel, Q extends TBasePaginationAPIFilter>(
  store: TPaginatedItemsStore<T, Q>,
) {
  const isMounted = useRef(true);
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  // setSearchParams is recreated whenever the location changes, but the sync outlives a render.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const [sync] = useState(
    () =>
      new StoreUrlSync(store, (queryString, options) => {
        if (isMounted.current) setSearchParamsRef.current(queryString, options);
      }),
  );

  // Runs on mount and on every Back/Forward; the sync ignores the echo of its own writes.
  useEffect(() => {
    sync.applyUrl(location.search);
  }, [location.search]);

  useEffect(() => {
    isMounted.current = true;

    const disposer = autorun(() => {
      sync.applyStore();
    });

    return () => {
      disposer();
      isMounted.current = false;
    };
  }, []);
}

export function parseUrlSearch(search: string | undefined | null) {
  if (!search) {
    return {};
  }

  // Parse url search string into object. Each key has an array of values (even single values are in array).
  const searchParams = new URLSearchParams(search);

  const result: { [key: string]: any } = {};
  searchParams.forEach((value, key) => {
    if (!(key in result)) {
      result[key] = [];
    }
    try {
      result[key].push(JSON.parse(value));
    } catch (e) {
      result[key].push(value);
    }
  });
  return result;
}

export function parsedUrlSearchArrayValuesToSingleValues(parsedUrlSearch: object) {
  // e.g. {'is_valid': [true], 'another_key': [7], 'key': [2, 6, 7]} -> {'is_valid': true, 'another_key': 7, 'key': [2, 6, 7]}
  return Object.entries(parsedUrlSearch).reduce(
    (acc, [key, value]) =>
      Array.isArray(value) && value.length === 1 ? { ...acc, [key]: value[0] } : { ...acc, [key]: value },
    {},
  );
}

export function toUrlSearchString(queryObj: object | undefined | null) {
  if (!queryObj || Object.keys(queryObj).length === 0) {
    return '';
  }

  const searchParams = new URLSearchParams();
  Object.entries(queryObj).forEach(
    ([key, value]) =>
      value !== undefined &&
      (Array.isArray(value) ? value.forEach((val) => searchParams.append(key, val)) : searchParams.append(key, value)),
  );

  return searchParams.toString();
}

export function isFrontendOutdated() {
  return window.appState.bundleHash !== window.appState.latestBundleHash;
}

export function containsHTMLTags(str: string) {
  const doc = new DOMParser().parseFromString(str, 'text/html');
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
}

export const convertHeicToJpeg = async (file: File) => {
  /**
   * Converts a HEIC image file to a JPEG format.
   *
   * @param {File} file - The HEIC file to be converted.
   *
   * @returns {Promise<File>} - A Promise that resolves to the converted JPEG file.
   *
   * @description
   * This function takes a HEIC file as input, uses a web worker and an external library (libheif)
   * to process and convert the HEIC file to JPEG format. The conversion is done asynchronously,
   * and the resulting JPEG file is returned in a Promise. The function also handles error reporting
   * through Sentry in case of conversion or worker errors.
   *
   * - It creates a temporary URL for the input file.
   * - It communicates with a web worker responsible for performing the conversion using the HEIF library.
   * - The converted file's name is derived from the original HEIC file but with a ".jpeg" extension.
   * - Any errors during conversion are captured by Sentry and propagated to the caller via rejection.
   * - The temporary URL is revoked after the conversion is complete.
   */

  const workerScriptUrl = `${window.appState.baseStaticUrl}heicToJpgConvertor.js`;
  const libHeifUrl = `${window.appState.baseStaticUrl}libheif-bundle.js`;

  const workerScriptContent = await (await fetch(workerScriptUrl)).blob();
  const url = URL.createObjectURL(workerScriptContent);

  // this needs to be a data/blob URL because Workers need to be served from the same origin as the main thread
  // therefore we cannot load the worker script from a different origin (e.g. cdn)
  // See https://developer.mozilla.org/en-US/docs/Web/API/Worker/Worker#security_considerations
  // and https://stackoverflow.com/questions/21913673/execute-web-worker-from-different-origin
  const worker = new Worker(url);

  const convertedFile: File = await new Promise((resolve, reject) => {
    const imageBlob = new Blob([file], { type: 'image/heic' });
    worker.postMessage({ file: imageBlob, libHeifUrl });

    worker.onmessage = (e) => {
      if (e.data.error) {
        window.Sentry.captureException(new Error(e.data.error));
        reject(new Error(e.data.error));
      } else if (e.data.blob) {
        const name = file.name.replace(/\.heic$/i, '.jpeg');
        resolve(new File([e.data.blob], name, { type: 'image/jpeg' }));
      }
    };

    worker.onerror = (err) => {
      window.Sentry.captureException(err);
      reject(err);
    };
  });

  worker.terminate();
  URL.revokeObjectURL(url);

  return convertedFile;
};

export const loadScript = (() => {
  // Cache for storing ongoing load promises
  const scriptPromises: Record<string, Promise<{ successful: boolean }>> = {};

  return (src: string, id: string): Promise<{ successful: boolean }> => {
    // If a script load has already been initiated, return the same promise
    if (scriptPromises[id]) {
      return scriptPromises[id];
    }

    scriptPromises[id] = new Promise((resolve, reject) => {
      const scriptElement = document.getElementById(id) as HTMLScriptElement;

      if (!scriptElement) {
        // Create the script element if it doesn't exist
        const script = document.createElement('script');
        script.src = src;
        script.id = id;

        const handleLoadScriptSuccess = () => {
          script.setAttribute('loaded', 'true'); // Mark as loaded
          resolve({ successful: true });
          delete scriptPromises[id]; // Clean up cache
        };

        const handleLoadScriptFail = (event: Event) => {
          reject(new Error(event.toString()));
          delete scriptPromises[id]; // Clean up cache
        };

        script.addEventListener('load', handleLoadScriptSuccess, { once: true });
        script.addEventListener('error', handleLoadScriptFail, { once: true });
        document.head.appendChild(script);
      } else if (scriptElement.getAttribute('loaded') === 'true') {
        // If the script is already loaded
        resolve({ successful: true });
      } else {
        // Script is not yet loaded, wait for it to finish loading
        scriptElement.addEventListener('load', () => resolve({ successful: true }), { once: true });
        scriptElement.addEventListener('error', (event: Event) => reject(new Error(event.toString())), { once: true });
      }
    });

    return scriptPromises[id];
  };
})();

/**
 * A cache interface for storing and retrieving data with timestamps
 */
interface CachedData<T> {
  timestamp: number;
  data: T;
}

/**
 * LocalStorageCache provides a convenient way to cache data in localStorage with
 * automatic expiration handling.
 */
export class LocalStorageCache<T> {
  private cacheKey: string;
  private prefix: string;
  private key: string;
  private timeoutMs: number;

  /**
   * Create a new LocalStorageCache instance
   *
   * @param key - The specific key for this cache instance
   * @param prefix - The prefix for the localStorage key
   * @param timeoutSec - Timeout in seconds (default: 7 days)
   */
  constructor(key: string, prefix: string = 'LOCAL_STORAGE_CACHE', timeoutSec: number = 7 * 24 * 60 * 60) {
    this.prefix = prefix;
    this.key = key;
    this.timeoutMs = timeoutSec * 1000;

    this.cacheKey = `${this.prefix}_${this.key}`;
  }

  /**
   * Store data in the cache
   *
   * @param data - The data to cache
   * @param isRetry - Whether this is a retry attempt after clearing space
   * @returns void
   */
  save(data: T, isRetry: boolean = false): void {
    const cachedData: CachedData<T> = {
      timestamp: Date.now(),
      data,
    };

    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(cachedData));
    } catch (error) {
      // Check if it's a quota exceeded error using MDN-specified identifiers
      // https://developer.mozilla.org/en-US/docs/Web/API/Storage_API/Storage_quotas_and_eviction_criteria#web_storage
      if (
        !isRetry &&
        error instanceof DOMException &&
        // Standard
        (error.name === 'QuotaExceededError' ||
          error.code === DOMException.QUOTA_EXCEEDED_ERR ||
          // Firefox
          error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
          // Safari/WebKit
          error.name === 'QUOTA_EXCEEDED_ERR')
      ) {
        // Clear old caches with the same prefix
        if (this.prefix) {
          this.clearCachesWithPrefix();
        } else {
          this.clear();
        }
        // Try again with isRetry flag to prevent infinite recursion
        this.save(data, true);
      } else if (window.Sentry) {
        // Either it's already a retry attempt or it's a different error
        window.Sentry.captureException(error);
      }
    }
  }

  /**
   * Load data from the cache if it exists and is not expired
   *
   * @returns The cached data or null if no valid cache exists
   */
  load(): T | null {
    const cachedDataString = localStorage.getItem(this.cacheKey);

    if (cachedDataString) {
      try {
        const cachedData: CachedData<T> = JSON.parse(cachedDataString);
        const currentTime = Date.now();

        // Check if cache is still valid (less than timeout)
        if (cachedData.timestamp && currentTime - cachedData.timestamp < this.timeoutMs) {
          return cachedData.data;
        }
      } catch (error) {
        if (window.Sentry) {
          window.Sentry.captureException(error);
        }
      }

      // Cache is expired or invalid, remove it
      localStorage.removeItem(this.cacheKey);
    }

    return null;
  }

  /**
   * Check if there is valid data in the cache
   *
   * @returns boolean indicating if valid cache exists
   */
  hasValidCache(): boolean {
    return this.load() !== null;
  }

  /**
   * Remove this cache entry
   */
  clear(): void {
    localStorage.removeItem(this.cacheKey);
  }

  /**
   * Clear all caches with the given prefix
   *
   * @param prefix - The prefix to match for clearing caches
   */
  private clearCachesWithPrefix(): void {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        keysToRemove.push(key);
      }
    }

    // Remove the collected keys
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}

export default {
  arrayMove,
  assert,
  containsHTMLTags,
  convertHeicToJpeg,
  currencyDisplay,
  currentLanguage,
  excerpt,
  formatDateAsMomentCalendar,
  formatLongDate,
  formatPrice,
  getForegroundColor,
  getOrderPortalDirectLink,
  indexArrayByProp,
  isFrontendOutdated,
  isImage,
  isUrlValid,
  loadScript,
  LocalStorageCache,
  makeAbsoluteUrl,
  monthNames,
  nl2br,
  nl2p,
  parseUrlSearch,
  rgetattr,
  roundHalfUp,
  toUrlSearchString,
  useAsyncEffect,
  usePrevValue,
  useRerender,
  weekDays,
};
